/**
 * SearchProblemCard — 검색 결과 한 건을 **보기 전용**으로 그린다(강의 작업 화면 · 설계 FE-20260912 §W01·§W03).
 *
 * 얼굴은 시험지 조립 창의 검색 카드(PaperComposeModal.jsx:2000-2208)와 같다 — 같은 문제가 두 화면에서
 * 다른 얼굴이면 읽는 사람이 「같은 문제인가」를 한 번 더 생각한다. 그래서 조각은 전부 그쪽이 쓰는 정본 부품이다:
 * 번호(ProblemIdLink) · 출신(getOriginDisplay) · 난이도 색(difficultyBadgeClass) · 개념(ConceptBadge compact) ·
 * 라벨(LabelBadges) · 본문(ProblemBodyPreview). 여기서 새로 정한 값은 없다.
 *
 * 조립 창과 다른 것 — **담기·고르기·끌기가 없다.** 강의 화면은 조건이 맞는지 「문제를 보고」 확인하는 자리라,
 * 고른 상태·담긴 상태·교체 모드가 뜻을 갖지 않는다(설계 §6: 시험지 제작 창 전체를 강의 화면으로 옮기지 않는다).
 * 그래서 조립 창에서 체크칸·[담기] 줄이던 머리를 배지 한 줄(flex-wrap)로 편다.
 * 「○ 없음」·분석 상태 배지는 싣지 않는다 — 비어 있는 것은 번호의 색과 title 이 이미 말한다(problemIdTone).
 *
 * 개념 배지는 사전으로 순번·유형을 채운다. 규칙은 조립 창의 withDict(PaperComposeModal.jsx:813)를 그대로 옮겼다:
 * 사전이 없으면 이름 있는 것만 order·type 없이, 있으면 id 로 찾아 이름이 비었으면 사전 이름으로 채우고 이름 없는 것은 거른다.
 * 누르면 설명 창을 여는 길(openConcept)은 아직 없다 — 조립 창의 창 상태에 묶여 있어 이번 범위 밖이다.
 */
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
import { getOriginDisplay, difficultyBadgeClass } from '../ProblemHubV2/problemApi';
import { ConceptBadge } from './conceptBadge';
import { LabelBadges } from './labelBadge';
import { conceptPairsOf } from './paperStats';
import ProblemBodyPreview from './ProblemBodyPreview';
import ProblemAnswerDisclosure from './ProblemAnswerDisclosure';

/**
 * 사전 배열 → Map(String(id) → 항목). 결과 20장이 같은 사전을 받으므로 사전 배열을 열쇠로 한 번만 만든다 —
 * 717개를 카드마다 다시 훑으면 한 번 그릴 때 만 번 넘게 비교한다(조립 창이 Map 을 쓰는 이유와 같다).
 */
const dictIndexCache = new WeakMap();
function dictIndexOf(dict) {
  if (!Array.isArray(dict) || dict.length === 0) return null;
  const hit = dictIndexCache.get(dict);
  if (hit) return hit;
  const m = new Map();
  dict.forEach((c) => { if (c?.id != null) m.set(String(c.id), c); });
  dictIndexCache.set(dict, m);
  return m;
}

/** 문항 한 건의 개념 배지 재료 [{ id, name, order, type }] — 조립 창 withDict(conceptPairsOf(it)) 와 같은 결과 */
export function conceptsOf(item, dict) {
  const pairs = conceptPairsOf(item);
  if (!Array.isArray(pairs) || pairs.length === 0) return [];
  const idx = dictIndexOf(dict);
  if (idx == null) return pairs.filter((c) => c.name).map((c) => ({ ...c, order: null, type: null }));
  return pairs.map((c) => {
    const m = c.id == null ? null : idx.get(String(c.id));
    return {
      ...c,
      name: c.name || m?.concept_name || m?.name || '',
      order: m?.order ?? null,
      type: m?.concept_type ?? null,
    };
  }).filter((c) => c.name);
}

export default function SearchProblemCard({ item, bodyHtml = null, dict = null }) {
  if (!item) return null;
  const id = item.problem_id ?? item.id ?? null;
  const origin = getOriginDisplay(item);
  const concepts = conceptsOf(item, dict);
  return (
    // 테두리는 늘 있다 — 한 문제가 어디서 끝나는지 세로로 훑어 알 수 있게(조립 창 카드와 같은 gray-200 · p-1.5)
    <article aria-label={id != null ? `${id}번 문제` : undefined} className="rounded-xl border border-gray-200 p-1.5">
      <div className="flex flex-wrap items-center gap-1">
        <ProblemIdLink id={id} item={item} className="shrink-0" />
        {origin && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium ${origin.cls}`}>{origin.text}</span>}
        {item.difficulty != null && (
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] ${difficultyBadgeClass(item.difficulty)}`}>난이도 {item.difficulty}</span>
        )}
        {/* asSpan — 누르는 길이 없으니 지금은 span 으로 서지만, 설명 창을 붙일 때 버튼 안의 버튼이 되지 않게 조립 창과 같이 둔다 */}
        {concepts.map((c, i) => (
          <ConceptBadge key={`${c.id ?? 'x'}-${c.name}-${i}`} name={c.name} order={c.order} type={c.type} compact asSpan />
        ))}
      </div>
      {/* 라벨은 개념 바로 아래, 본문 위 — 고르는 단서 둘이 본문을 사이에 두고 갈라지지 않게(조립 창 2187 주석) */}
      <LabelBadges labels={item.labels} className="mt-0.5 flex flex-wrap items-center gap-1" />
      {/* 본문 상자(radius16·gray-300·p-2.5)는 ProblemBodyPreview 가 갖는다 — 번호·개념·라벨은 그 밖에 둔다(설계 표 「문제 결과」) */}
      {bodyHtml != null && bodyHtml !== ''
        ? <ProblemBodyPreview html={bodyHtml} />
        : <ProblemBodyPreview fallback={item.preview || <span className="text-gray-300">본문 미리보기 없음</span>} />}
      {id != null && <ProblemAnswerDisclosure problemId={id} />}
    </article>
  );
}
